import { create } from "zustand";
import {
  getWalletBalance,
  getWalletTransactions,
  getWalletStorage,
} from "../api/wallet.api";
import { subscribe } from "../lib/walletEvents";

const useWalletStore = create((set, get) => ({
  balance: null,           // null until loaded
  storage: null,           // { used_bytes, cap_bytes, cap_gb, unlimited, percent_used }
  transactions: [],
  loading: false,
  lastFetched: null,

  fetchBalance: async () => {
    try {
      const res = await getWalletBalance();
      set({ balance: res.data, lastFetched: Date.now() });
      return res.data;
    } catch (e) {
      // 401/403 etc — leave existing balance, badge will hide
      return null;
    }
  },

  fetchStorage: async () => {
    try {
      const res = await getWalletStorage();
      set({ storage: res.data });
      return res.data;
    } catch (e) {
      return null;
    }
  },

  fetchTransactions: async (limit = 50) => {
    set({ loading: true });
    try {
      const res = await getWalletTransactions(limit);
      set({ transactions: res.data.transactions || [] });
      return res.data;
    } catch (e) {
      return null;
    } finally {
      set({ loading: false });
    }
  },

  // Subscribe to "wallet_changed" event so any AI action that debited credits
  // triggers a balance refetch. Called once at app boot.
  initWalletEventListeners: () => {
    if (get()._listenersInstalled) return;
    set({ _listenersInstalled: true });
    subscribe("wallet_changed", () => {
      get().fetchBalance();
    });
  },

  // 2026-06-18 — clear all wallet state. Called by auth.store on
  // sign-out / sign-in so the previous account's balance + transactions
  // + storage do not briefly flash on the new account's first render.
  // Fixes the Adhish session-confusion class of bug where a stale
  // cached balance from the previous session was displayed before the
  // first /api/wallet/balance/ fetch resolved against the new JWT.
  reset: () => set({
    balance: null,
    storage: null,
    transactions: [],
    loading: false,
    lastFetched: null,
  }),
}));

export default useWalletStore;
